import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {
  ComponentProps,
  JSXElementConstructor,
  ReactNode,
  useState,
} from 'react';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';

type ComponentType = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

interface Props<T extends ComponentType> {
  ids: number[];
  component?: T;
  componentProps?: ComponentProps<T>;
  children?: ReactNode;
  onDone?: () => void;
}

const AssetMachineItemsPurgeEventsButton = <
  T extends ComponentType = typeof Button,
>({
  ids,
  component,
  componentProps,
  children,
  onDone,
}: Props<T>) => {
  const Component = component ?? Button;
  const {enqueueSnackbar} = useSnackbar();

  const [isOpened, setIsOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setIsOpened(true);
  const closeModal = () => setIsOpened(false);

  const submit = async () => {
    setIsSubmitting(true);

    try {
      if (ids.length) {
        await API.post(`${apiBaseUrl}/purge`, {
          asset_machine_ids: ids,
          action: 'data',
        });
      }

      const message = 'Transactions purged successfully';
      enqueueSnackbar(message, {
        variant: 'success',
        action: CloseSnackbarAction,
      });
      onDone?.();
      closeModal();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Component {...componentProps} onClick={openModal}>
        {children}
      </Component>

      <Dialog open={isOpened} onClose={closeModal}>
        <DialogTitle>Purge Transactions</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to purge all transaction data from the
            selected device?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsOpened(false)}>Cancel</Button>

          <LoadingButton
            variant="contained"
            loading={isSubmitting}
            autoFocus
            onClick={() => submit()}
            color="error"
          >
            Purge Transactions
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetMachineItemsPurgeEventsButton;
